import { Link } from 'react-router-dom'
import { Lightbulb, FileText, Search, Users, BotMessageSquare, BookOpen } from 'lucide-react'
import { DocSection } from '@/components/docs/doc-section'
import { DocPageHeader } from '@/components/docs/doc-page-header'
import { DocNav } from '@/components/docs/doc-nav'
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert'
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card'

const platformApps = [
  {
    title: 'PAC',
    description: 'Create, manage, and deploy ABAC policies that control who can access what data and under which conditions.',
    icon: FileText,
    href: '/pac/dashboard',
  },
  {
    title: 'Data Discovery',
    description: 'Connect to your databases, scan tables, and automatically classify sensitive data like PII, PHI, and financial records.',
    icon: Search,
    href: '/discovery/dashboard',
  },
  {
    title: 'Identity Store',
    description: 'Manage user attributes, sync with identity providers, and maintain the subject data that powers every access decision.',
    icon: Users,
    href: '/identity/dashboard',
  },
  {
    title: 'poli-x AI',
    description: 'Describe what you need in plain language and let the AI assistant draft production-ready XACML policies for you.',
    icon: BotMessageSquare,
    href: '/polix-ai',
  },
] as const

const docTopics = [
  { title: 'What is ABAC?', href: '/docs/abac' },
  { title: 'XACML Basics', href: '/docs/xacml' },
  { title: 'Attributes', href: '/docs/attributes' },
  { title: 'Policies', href: '/docs/policies' },
  { title: 'Request Evaluation', href: '/docs/evaluation' },
  { title: 'Architecture', href: '/docs/architecture' },
] as const

export function GettingStartedPage() {
  return (
    <>
      <DocPageHeader icon={BookOpen} title="Getting Started" />

      <div className="flex flex-1 flex-col gap-6 overflow-auto p-6 max-w-4xl">
        <Alert>
          <Lightbulb className="h-4 w-4" />
          <AlertTitle>New to access control?</AlertTitle>
          <AlertDescription>
            Start with{' '}
            <Link to="/docs/abac" className="font-medium underline underline-offset-4">
              What is ABAC?
            </Link>{' '}
            to learn the fundamentals before diving into the platform.
          </AlertDescription>
        </Alert>

        <DocSection title="Welcome to poli-x">
          <p className="text-sm text-muted-foreground leading-relaxed">
            poli-x is an enterprise Attribute-Based Access Control platform built on the XACML 3.0
            standard. It replaces brittle role-based permissions with intelligent, context-aware
            data protection. Instead of maintaining sprawling role hierarchies, you define policies
            that evaluate real-time attributes — like a user's department, the sensitivity of a
            database column, or the time of day — to make fine-grained access decisions
            automatically.
          </p>
        </DocSection>

        <DocSection title="Platform Apps">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {platformApps.map((app) => {
              const Icon = app.icon
              return (
                <Link key={app.title} to={app.href} className="group">
                  <Card className="h-full transition-colors group-hover:border-foreground/20">
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4 text-muted-foreground" />
                        <CardTitle className="text-sm">{app.title}</CardTitle>
                      </div>
                      <CardDescription className="text-xs leading-relaxed">
                        {app.description}
                      </CardDescription>
                    </CardHeader>
                  </Card>
                </Link>
              )
            })}
          </div>
        </DocSection>

        <DocSection title="What You'll Learn">
          <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
            {docTopics.map((topic, index) => (
              <li key={topic.href} className="leading-relaxed">
                <Link
                  to={topic.href}
                  className="font-medium text-foreground underline underline-offset-4 hover:text-foreground/80"
                >
                  {topic.title}
                </Link>
                {index === 0 && ' — Understand why attribute-based access control exists and when it outperforms RBAC.'}
                {index === 1 && ' — Learn the standard that powers poli-x policies, requests, and decisions.'}
                {index === 2 && ' — The building blocks: subject, resource, action, and environment attributes.'}
                {index === 3 && ' — How to write rules that combine attributes into access decisions.'}
                {index === 4 && ' — Walk through how the PDP evaluates a request from start to finish.'}
                {index === 5 && ' — See how PAP, PDP, PEP, and PIP work together in production.'}
              </li>
            ))}
          </ol>
        </DocSection>

        <DocNav next={{ title: 'What is ABAC?', url: '/docs/abac' }} />
      </div>
    </>
  )
}
